import { useEffect, useRef, useState } from 'react';
import { OrderStatus } from '../../../../api/generated-catch-backend/model';

const POLL_INTERVAL_MS = 2000; // 2 seconds
const TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes

interface UseLabOrderPollingProps {
  orderStatus: string | undefined;
  refetchOrder: () => void;
}

/**
 * Hook to poll for lab order status changes when order is pending signature.
 * Polls every 2 seconds for up to 2 minutes.
 *
 * @param orderStatus - Current order status
 * @param refetchOrder - Function to refetch order data
 * @returns Object with isPolling and hasTimedOut flags
 */
export const useLabOrderPolling = ({
  orderStatus,
  refetchOrder,
}: UseLabOrderPollingProps) => {
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isPolling =
    orderStatus === OrderStatus.order_created_pending_signature &&
    !hasTimedOut;

  useEffect(() => {
    // Only poll if status is pending signature
    if (orderStatus !== OrderStatus.order_created_pending_signature) {
      // Clear any existing intervals and reset timeout state
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setHasTimedOut(false);
      return;
    }

    // Don't start polling if already timed out
    if (hasTimedOut) {
      return;
    }

    // Start polling
    intervalRef.current = setInterval(() => {
      refetchOrder();
    }, POLL_INTERVAL_MS);

    // Set timeout to stop polling after 2 minutes
    timeoutRef.current = setTimeout(() => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setHasTimedOut(true);
    }, TIMEOUT_MS);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [orderStatus, refetchOrder, hasTimedOut]);

  return { isPolling, hasTimedOut };
};
